import type { FC } from 'react';
import { memo, useCallback } from 'react';

import type { TModalProps } from '~components/Modals/types';
import { routes } from '~constants/routes';
import { annals } from '~features/annals/hooks';
import { ReportModal } from '~features/report/ui/ReportModal';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { useRouter } from '~i18n/navigation';
import { useReportAndDeleteBotMutation } from '~services/api/bots/endpoints';

export const ReportCharacterModal: FC<
	TModalProps & {
		botId: number;
		onReported?: () => void;
	}
> = memo(({ open, onClose, locked, botId }) => {
	const router = useRouter();
	const { userId } = useSession();
	const { bot } = useCurrentBot({ recipientId: botId });
	const t = useTranslations('Report.reportBot');
	const reportCharacterCategories = (
		(t.raw('reportBotCategories') || []) as { value: string }[]
	).map((it) => it.value);
	const [reportAndDeleteBot] = useReportAndDeleteBotMutation();
	const sendReportBot = annals.report.useUGCBotReport({
		botId: bot?.clientId?.toString(),
		botName: bot.name,
		status: bot.access || 'public',
	});

	const action = useCallback(
		async (selectedCategory: string, reportText?: string) => {
			if (!reportText) return;
			router.replace(routes.chats.home);
			await reportAndDeleteBot({
				reason: selectedCategory,
				reportText,
				recipientId: botId.toString(),
				userId,
			});

			sendReportBot({ type: selectedCategory, comment: reportText });
		},
		[botId, reportAndDeleteBot, router, sendReportBot, userId]
	);

	return (
		<ReportModal
			title={t('reportBotModalTitle')}
			subtitle={t('reportBotModalSubtitle', { name: bot.name || '' })}
			buttonText={t('reportBotModalButton')}
			open={open}
			locked={locked}
			categories={reportCharacterCategories}
			note={t('reportBotModalNote')}
			action={action}
			onClose={onClose}
			reportCommentRequired
		/>
	);
});

ReportCharacterModal.displayName = 'ReportCharacterModal';
